import { AlertTriangle, GitBranch } from "lucide-react";
import { Badge } from "../../../../../components/tailwind/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../components/tailwind/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../components/tailwind/tooltip";
import { workflowTheme } from "../../../utils/workflowBuilderTheme";

function PassBadge({ passRate }) {
  if (passRate == null) {
    return <span className={`text-xs ${workflowTheme.textMuted}`}>—</span>;
  }

  const className =
    passRate === 100
      ? "border-none bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-950/40"
      : "border-none bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-950/40";

  return (
    <Badge variant="secondary" className={`text-[11px] font-bold px-1.5 py-0 ${className}`}>
      {passRate}%
    </Badge>
  );
}

export function WorkflowAnalyticsFunnel({ nodeRows }) {
  return (
    <Card className={`shadow-sm ${workflowTheme.card}`}>
      <CardHeader className="p-4 md:p-5 pb-2 space-y-1">
        <CardTitle className={`flex items-center gap-2 text-sm ${workflowTheme.textPrimary}`}>
          <GitBranch className={`h-4 w-4 shrink-0 ${workflowTheme.textSecondary}`} />
          Node performance
        </CardTitle>
        <p className={`text-xs font-normal ${workflowTheme.textSecondary}`}>
          Entries, exits, and issues per step this month
        </p>
      </CardHeader>
      <CardContent className="p-4 md:p-5 pt-2">
        <TooltipProvider delayDuration={200}>
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className={`text-left text-[11px] uppercase tracking-wide ${workflowTheme.textMuted}`}>
                  <th className="pb-2.5 font-semibold">Node</th>
                  <th className="pb-2.5 font-semibold text-right">Entries</th>
                  <th className="pb-2.5 font-semibold text-right">Exits</th>
                  <th className="pb-2.5 font-semibold text-right">Fallbacks</th>
                  <th className="pb-2.5 font-semibold text-right">Errors</th>
                  <th className="pb-2.5 font-semibold text-right">Avg time</th>
                  <th className="pb-2.5 font-semibold text-right">Pass</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {nodeRows.map((node) => {
                  const incompleteEntries = node.entries === 0 && node.exits > 0;
                  return (
                    <tr key={node.nodeId} className="align-middle">
                      <td className={`py-2.5 pr-3 ${workflowTheme.textPrimary}`}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-medium truncate">{node.label}</span>
                          {incompleteEntries && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="shrink-0 text-amber-600 dark:text-amber-400"
                                  aria-label="Incomplete entry tracking"
                                >
                                  <AlertTriangle className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                                Exits recorded with 0 entries — entry tracking may be incomplete
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <span className={`block text-[11px] font-mono ${workflowTheme.textMuted}`}>
                          {node.nodeId}
                        </span>
                      </td>
                      <td className={`py-2.5 text-right tabular-nums ${workflowTheme.textPrimary}`}>
                        {node.entries}
                      </td>
                      <td className={`py-2.5 text-right tabular-nums ${workflowTheme.textPrimary}`}>
                        {node.exits}
                      </td>
                      <td
                        className={`py-2.5 text-right tabular-nums ${
                          node.fallbacks > 0
                            ? "text-orange-600 dark:text-orange-400 font-medium"
                            : workflowTheme.textPrimary
                        }`}
                      >
                        {node.fallbacks}
                      </td>
                      <td
                        className={`py-2.5 text-right tabular-nums ${
                          node.errors > 0
                            ? "text-red-600 dark:text-red-400 font-medium"
                            : workflowTheme.textPrimary
                        }`}
                      >
                        {node.errors}
                      </td>
                      <td className={`py-2.5 text-right ${workflowTheme.textSecondary}`}>
                        {node.avgDurationLabel}
                      </td>
                      <td className="py-2.5 text-right">
                        <PassBadge passRate={node.passRate} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}
